/* @flow */

import { createSelector } from '@reduxjs/toolkit';
import {
  hydrateUser,
  getUserStatistics,
} from '@braindate/domain/lib/user/util';

import type { User, UserStatistics } from '@braindate/domain/lib/user/type';
import type { State } from 'src/shared/core/type/reduxTypes';
import type { Languages } from '../type/languagesTypes';

/*
 |------------------------------------------------------------------------------
 | Slices
 |------------------------------------------------------------------------------
 */

const getAppUser = (state) => state.app.user;
const getAppLanguages = (state) => state.app.languages;
const getEntities = (state) => state.domain.entities;
const getUserIdFromProps = (state, props: Object) => props.id;

/*
 |------------------------------------------------------------------------------
 | Exports
 |------------------------------------------------------------------------------
 */

export const getUser: (state: State, props: Object) => ?User = createSelector(
  [getUserIdFromProps, getEntities],
  (id, entities): any => {
    if (id) {
      return hydrateUser(id, entities);
    }
  },
);

export const getSelf: (state: State) => ?User = createSelector(
  [getAppUser, getEntities],
  (id, entities) => {
    if (id) {
      return hydrateUser(id, entities);
    }
  },
);

export const getUserId: (state: State) => number = createSelector(
  [getAppUser],
  (x) => x,
);

export const getSelfStats: (state: State) => ?UserStatistics = createSelector(
  [getSelf],
  (selfUser) => {
    if (selfUser) {
      return getUserStatistics(selfUser);
    }
  },
);

export const getPossibleLanguages: (state: State) => Languages = createSelector(
  [getAppLanguages],
  (languages) => languages.result,
);

export const getLanguageByKey: (state: State, key: string) => string =
  createSelector(
    [getAppLanguages, (state: State, key: string) => key],
    (languages, key) => languages.result[key],
  );
export const getLanguagesByKeys: (
  keys: Array<string>,
) => (state: State) => string = (keys) =>
  createSelector(
    [
      (state: State) =>
        keys.map((k) => getLanguageByKey(state, k)).filter(Boolean),
    ],
    (r) => r,
  );
